import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Select, Empty, Spinner, fmtBRL, fmtKg } from "@/components/ui/Primitives";
import Pagination from "@/components/ui/Pagination";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, Search, Filter, Trash2, Pencil, Printer } from "lucide-react";

export default function InvoicesList() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [pageSize, setPageSize] = useState(() => Number(searchParams.get("ps") || 25));
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [qDebounced, setQDebounced] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(initialStatus);
  const [unidade, setUnidade] = useState(searchParams.get("unidade") || "");
  const [err, setErr] = useState("");

  // Sync com URL (usuário volta pra rota com query param diferente)
  useEffect(() => { setStatus(initialStatus); /* eslint-disable-next-line */ }, [initialStatus]);

  // Debounce search input → server query
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  // Reset to first page whenever filters/search change
  useEffect(() => { setPage(1); }, [status, unidade, qDebounced, pageSize]);

  // Persiste filtros na URL para preservar ao voltar do detalhe
  useEffect(() => {
    const next = new URLSearchParams();
    if (status) next.set("status", status);
    if (unidade) next.set("unidade", unidade);
    if (qDebounced) next.set("q", qDebounced);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 25) next.set("ps", String(pageSize));
    setSearchParams(next, { replace: true });
  }, [status, unidade, qDebounced, page, pageSize, setSearchParams]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (status) params.status = status;
      if (unidade) params.unidade = unidade;
      if (qDebounced) params.q = qDebounced;
      const { data } = await api.get("/invoices", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, unidade, qDebounced, page, pageSize]);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta NF?")) return;
    try { await api.delete(`/invoices/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  return (
    <Layout
      title="Notas Fiscais de Entrada"
      subtitle="Compras de cebola in natura"
      actions={
        !initialStatus && (
          <Link to="/invoices/new">
            <Button data-testid="new-invoice-button"><Plus size={16} /> Nova NF</Button>
          </Link>
        )
      }
    >
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nº da NF, fornecedor ou motorista..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                data-testid="invoice-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="invoice-status-filter">
              <option value="">Todas</option>
              <option value="bruto">Pendentes</option>
              <option value="em_beneficiamento">Em beneficiamento</option>
              <option value="beneficiada">Finalizadas</option>
            </Select>
          </div>
          <div className="md:col-span-3">
            <Select label="Unidade" value={unidade} onChange={(e) => setUnidade(e.target.value)} data-testid="invoice-unidade-filter">
              <option value="">Todas</option>
              <option value="Matriz VGS">Matriz VGS</option>
              <option value="Araxá">Araxá</option>
              <option value="Ituporanga">Ituporanga</option>
            </Select>
          </div>
          <div className="md:col-span-2 text-right">
            <span className="text-xs text-ink-500"><Filter size={12} className="inline" /> {total} resultado(s)</span>
          </div>
        </div>
      </Card>

      {err && <div className="text-sm text-danger mb-4">{err}</div>}

      <FreteAlertBanner items={items} />

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : (
        <Card className="overflow-hidden">
          {items.length === 0 ? (
            <Empty title="Nenhuma NF encontrada" hint="Cadastre uma nova entrada para começar." />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-ink-200 sticky top-0">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3">NF / Romaneio</th>
                  <th className="px-4 py-3">Fornecedor</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Unidade</th>
                  <th className="px-4 py-3 text-right">Peso Líq.</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3 w-1">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const goDetail = (e) => { if (e.target.closest("button")) return; nav(`/invoices/${i.id}`); };
                  return (
                  <tr key={i.id} onClick={goDetail} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors cursor-pointer" data-testid={`invoice-row-${i.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold text-ink-900">#{i.nota_numero}</div>
                        {i.ja_beneficiada && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                            title="Comprada já beneficiada — não entra no custo por saco"
                            data-testid={`invoice-ja-beneficiada-${i.id}`}
                          >
                            já benef.
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-500 font-mono">RM-{String(i.romaneio_numero || 0).padStart(4, "0")}</div>
                      <FreteBadges inv={i} />
                    </td>
                    <td className="px-4 py-3 text-ink-700">{i.fornecedor}</td>
                    <td className="px-4 py-3 capitalize">{i.tipo_cebola}</td>
                    <td className="px-4 py-3 text-ink-700 text-xs">{i.unidade || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtKg(i.peso_liquido)}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmtBRL(i.valor_nf || i.valor_total_caixas)}</td>
                    <td className="px-4 py-3"><Badge tone={i.status}>{
                      i.status === "bruto" ? "pendente"
                      : i.status === "em_beneficiamento" ? "em beneficiamento"
                      : i.status === "beneficiada" ? "finalizada"
                      : i.status.replace("_", " ")
                    }</Badge></td>
                    <td className="px-4 py-3 text-ink-700">{fmtDate(i.data_entrada)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <a
                        href={`/invoices/${i.id}/romaneio`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex p-1.5 rounded-md hover:bg-emerald-50 text-emerald-700 transition-colors"
                        title="Imprimir Romaneio do Produtor"
                        data-testid={`invoice-print-produtor-${i.id}`}
                      >
                        <Printer size={16} />
                      </a>
                      <a
                        href={`/invoices/${i.id}/romaneio-custos`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex p-1.5 rounded-md hover:bg-amber-100 text-amber-700 transition-colors"
                        title="Imprimir Romaneio de Custos"
                        data-testid={`invoice-print-custos-${i.id}`}
                      >
                        <Printer size={16} />
                      </a>
                      <button onClick={() => nav(`/invoices/${i.id}/edit`)} className="p-1.5 rounded-md hover:bg-emerald-100 text-brand-500 transition-colors" title="Editar" data-testid={`invoice-edit-${i.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => onDelete(i.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger transition-colors" title="Excluir" data-testid={`invoice-delete-${i.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={setPageSize}
            testidPrefix="invoices-pagination"
          />
        </Card>
      )}
    </Layout>
  );
}

const semMotorista = (i) => i.tipo_operacao !== "terceiros" && !(i.motorista || "").trim();
const semFrete = (i) => i.tipo_operacao !== "terceiros"
  && !(Number(i.frete_total) > 0) && !(Number(i.frete_por_ton) > 0) && !(Number(i.frete_total_manual) > 0)
  && !(Number(i.beneficiamento?.custos?.frete) > 0);

const FreteBadges = ({ inv }) => {
  const m = semMotorista(inv), f = semFrete(inv);
  if (!m && !f) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {f && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200" data-testid={`invoice-sem-frete-${inv.id}`}>SEM FRETE</span>}
      {m && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-300" data-testid={`invoice-sem-motorista-${inv.id}`}>SEM MOTORISTA</span>}
    </div>
  );
};

const FreteAlertBanner = ({ items }) => {
  const nf = items.filter(semFrete).length;
  const nm = items.filter(semMotorista).length;
  if (!nf && !nm) return null;
  return (
    <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900 flex flex-wrap gap-x-6 gap-y-1" data-testid="invoice-frete-alert">
      <span className="font-semibold">Atenção nas cargas listadas:</span>
      {nf > 0 && <span data-testid="invoice-frete-alert-frete"><strong>{nf}</strong> sem valor de frete definido</span>}
      {nm > 0 && <span data-testid="invoice-frete-alert-motorista"><strong>{nm}</strong> sem motorista definido</span>}
      <span className="text-xs text-amber-700">Abra a NF e clique em editar para completar.</span>
    </div>
  );
};
